import React from "react";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import CopyField from "@/components/CopyField";
import { toast } from "sonner";
import {
  authedFileUrl, downloadFile,
} from "@/components/propertyDetail/helpers";
import {
  Sparkle, Spinner, Microphone, Stop, DownloadSimple, FilePdf,
  CurrencyEur, Users, Check, ArrowRight, Copy,
} from "@phosphor-icons/react";
import { usePropertyDetail } from "../context";

/**
 * AutopilotTab — "Analisi annuncio" (ex Autopilot). Contiene:
 *  - Card analisi + nota vocale agente
 *  - Stima prezzo (iter 261: spostata qui da Pagina online)
 *  - Hero foto migliorate
 *  - Titolo + descrizione autopilot
 *  - CTA Pubblica subito
 *  - Top 3 problemi
 *  - Toggle "mostra di più" → score, short, checklist
 *  - Seller mode (presentazione per il venditore)
 *
 * Estratto da PropertyDetail.jsx iter 247 (refactor completo).
 */
export default function AutopilotTab() {
  const {
    id, photos, listing, autopilot, autopilotLoading, autopilotShowMore, setAutopilotShowMore,
    sellerMode, setSellerMode, voiceTranscript,
    onRunAutopilot,
    estimate, estimating, onEstimate,
    setPublishOpen, setTab,
  } = usePropertyDetail();

  return (
      <div className="mt-8 space-y-6">
        {sellerMode && autopilot ? (
          <div className="card-flat p-8 lg:p-12 bg-[#7C8B6E] text-[#FFFFFF]">
            <div className="flex items-start justify-between gap-4 mb-8">
              <div>
                <span className="text-xs uppercase tracking-[0.22em] opacity-70">Per il venditore</span>
                <h2 className="font-editorial text-4xl lg:text-5xl mt-3 leading-tight">
                  Una presentazione migliore<br/><span className="italic-accent text-[#F1F4ED]">vende prima.</span>
                </h2>
              </div>
              <button onClick={() => setSellerMode(false)} className="text-sm underline opacity-80 hover:opacity-100" data-testid="seller-mode-exit">Esci</button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {photos.slice(0, 1).map((p) => p.enhanced_path && (
                <div key={p.id}>
                  <span className="text-xs uppercase tracking-widest opacity-60">Prima</span>
                  <img alt="prima" src={authedFileUrl(p.original_path)} className="w-full mt-3 rounded-sm" />
                </div>
              ))}
              {photos.slice(0, 1).map((p) => p.enhanced_path && (
                <div key={`a-${p.id}`}>
                  <span className="text-xs uppercase tracking-widest opacity-60">Dopo</span>
                  <img alt="dopo" src={authedFileUrl(p.enhanced_path)} className="w-full mt-3 rounded-sm" />
                </div>
              ))}
            </div>
            <p className="text-lg leading-relaxed max-w-3xl whitespace-pre-line opacity-95">
              {autopilot.seller_pitch}
            </p>
          </div>
        ) : (
          <>
            <div className="card-flat p-5 lg:p-6" data-testid="autopilot-card">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <span className="overline">Controllo qualità</span>
                  <h3 className="font-editorial text-xl mt-1.5">Ricontrolliamo prima di pubblicare</h3>
                  <p className="mt-1.5 text-[12.5px] text-[#6B6A65] max-w-md leading-snug">
                    Diamo un voto all'annuncio e trovi i 3 motivi per cui sta <em>perdendo richieste</em> · 1 credito.
                  </p>
                </div>
                <button
                  onClick={onRunAutopilot}
                  disabled={autopilotLoading}
                  className="btn-primary text-sm disabled:opacity-60"
                  data-testid="autopilot-run-btn"
                >
                  {autopilotLoading ? (<><Spinner size={16} className="animate-spin" /> Sto analizzando...</>) : (<><Sparkle size={16} weight="fill" /> {autopilot ? "Rianalizza" : "Analizza l'annuncio"}</>)}
                </button>
              </div>

              {/* iter v28 r24 — Box vocale spostato nel tab "Testo" (DescrizioneTab).
                  Qui mostriamo solo un chip riassuntivo della nota corrente (se
                  presente) per ricordare all'agente che l'analisi la considera. */}
              {voiceTranscript && (
                <div className="mt-4 p-3 bg-[#FAF9F4] border border-[#E5E7E0] rounded-md flex items-start gap-2.5" data-testid="autopilot-note-chip">
                  <Microphone size={14} weight="bold" className="text-[#7C8B6E] mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#7C8B6E]">Nota considerata nell'analisi</div>
                    <div className="text-[12.5px] italic text-[#1C1C1A] mt-1 line-clamp-2">"{voiceTranscript}"</div>
                  </div>
                  <button
                    onClick={() => setTab("descrizione")}
                    className="text-[11px] text-[#7C8B6E] hover:underline font-semibold whitespace-nowrap"
                    data-testid="autopilot-edit-note"
                  >
                    Modifica →
                  </button>
                </div>
              )}
            </div>

            {/* Stima prezzo — compattata in iter 310 r18 */}
            <div className="card-flat p-5 lg:p-6 mt-4" data-testid="estimate-card">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="max-w-md">
                  <span className="overline">Stima prezzo</span>
                  <h3 className="font-editorial text-xl mt-1.5">Quanto può <span className="italic-accent text-[#7C8B6E]">valere</span>?</h3>
                  <p className="mt-1.5 text-[12.5px] text-[#6B6A65] leading-snug">
                    Range di mercato indicativo · 1 credito. Non costituisce perizia ufficiale.
                  </p>
                </div>
                {!estimate && (
                  <button onClick={onEstimate} disabled={estimating} className="btn-primary disabled:opacity-60 text-sm" data-testid="estimate-btn">
                    {estimating ? <><Spinner size={16} className="animate-spin" /> Stima…</> : <><CurrencyEur size={16} /> Genera stima</>}
                  </button>
                )}
              </div>
              {estimate && (
                <div className="mt-5">
                  <div className="grid grid-cols-3 gap-3" data-testid="estimate-result">
                    <div className="border border-[#E5E7E0] p-4 text-center">
                      <div className="overline">Minimo</div>
                      <div className="mt-1 font-editorial text-xl">€ {Number(estimate.min).toLocaleString("it-IT")}</div>
                    </div>
                    <div className="border-2 border-[#7C8B6E] bg-[#F1F4ED] p-4 text-center">
                      <div className="overline">Probabile</div>
                      <div className="mt-1 font-editorial text-2xl text-[#7C8B6E]">€ {Number(estimate.likely).toLocaleString("it-IT")}</div>
                    </div>
                    <div className="border border-[#E5E7E0] p-4 text-center">
                      <div className="overline">Massimo</div>
                      <div className="mt-1 font-editorial text-xl">€ {Number(estimate.max).toLocaleString("it-IT")}</div>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-[#1C1C1A] leading-relaxed"><strong>Ragionamento:</strong> {estimate.reasoning}</p>
                  <p className="mt-2 text-xs text-[#6B6A65]">Affidabilità: <strong>{estimate.confidence}</strong>. Non costituisce perizia ufficiale.</p>
                  <button onClick={onEstimate} disabled={estimating} className="btn-outline mt-3 text-sm" data-testid="re-estimate-btn">
                    {estimating ? "Rigenero…" : "Rigenera stima"}
                  </button>
                </div>
              )}
            </div>

            {autopilot && (
              <>
                {/* 1️⃣ HERO — Foto ottimizzate (valore immediato) */}
                {(() => {
                  const enhancedPhotos = photos.filter(
                    (p) => (p.photo_kind || "photo") === "photo" && p.enhanced_path,
                  );
                  if (enhancedPhotos.length === 0) return null;
                  const hero = enhancedPhotos[0];
                  const rest = enhancedPhotos.slice(1, 5);
                  return (
                    <div className="card-flat p-6 lg:p-8" data-testid="autopilot-hero">
                      <div className="flex items-end justify-between gap-3 mb-5 flex-wrap">
                        <div>
                          <span className="overline">Foto pronte da pubblicare</span>
                          <h3 className="font-editorial text-2xl mt-2">
                            {enhancedPhotos.length} {enhancedPhotos.length === 1 ? "foto migliorata" : "foto migliorate"} da Pronto
                          </h3>
                        </div>
                        <button
                          onClick={() => downloadFile(hero.enhanced_path, "pronto-foto.jpg")}
                          className="btn-outline text-sm"
                          data-testid="autopilot-download-hero"
                        >
                          <DownloadSimple size={16} weight="regular" /> Scarica foto
                        </button>
                      </div>
                      <BeforeAfterSlider
                        beforeSrc={authedFileUrl(hero.original_path)}
                        afterSrc={authedFileUrl(hero.enhanced_path)}
                        height="h-[55vh]"
                        fit="contain"
                      />
                      {rest.length > 0 && (
                        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {rest.map((p) => (
                            <img
                              key={p.id}
                              alt=""
                              src={authedFileUrl(p.enhanced_path)}
                              className="aspect-[4/3] object-cover rounded-sm border border-[#E5E7E0]"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* 2️⃣ Titolo + Descrizione (annuncio pronto)
                    iter 264: descrizione ora multi-colonna su desktop per
                    evitare il "blocco di testo strettissimo e lunghissimo"
                    quando l'annuncio supera ~800 caratteri. */}
                <div className="card-flat p-5 lg:p-6" data-testid="autopilot-listing">
                  <span className="overline">Annuncio pronto</span>
                  <h2 className="font-editorial text-2xl lg:text-3xl mt-2 leading-tight" data-testid="autopilot-title">
                    {autopilot.title}
                  </h2>
                  <div className="mt-4">
                    <CopyField label="Titolo" value={autopilot.title} testId="copy-ap-title" />
                  </div>
                  <div className="mt-2 pt-2.5" data-testid="copy-ap-description">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <span className="overline">Descrizione</span>
                      <button
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(autopilot.description || "");
                            toast.success("Descrizione copiata");
                          } catch { toast.error("Impossibile copiare"); }
                        }}
                        className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#7C8B6E] hover:text-[#1C1C1A] transition-colors"
                        data-testid="copy-ap-description-copy-btn"
                      >
                        <Copy size={12} weight="bold" /> Copia testo
                      </button>
                    </div>
                    <div
                      className="text-[14.5px] leading-[1.7] text-[#1C1C1A] whitespace-pre-line lg:columns-2 lg:gap-8"
                      style={{ columnRule: "1px solid #E5E7E0" }}
                    >
                      {autopilot.description || "—"}
                    </div>
                  </div>
                </div>

                {/* 2️⃣bis CTA — Pubblica subito (chiude il loop voce → annuncio) */}
                <div
                  className="relative overflow-hidden p-8 lg:p-10 text-[#FFFFFF]"
                  style={{ background: "#7C8B6E" }}
                  data-testid="publish-now-cta-card"
                >
                  <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
                    <div className="max-w-xl">
                      <span className="text-xs uppercase tracking-[0.18em] text-[#FFFFFF]/60">
                        Pronto da pubblicare
                      </span>
                      <h3 className="font-editorial text-3xl lg:text-4xl mt-2 leading-tight">
                        Mettilo online in 60 secondi
                      </h3>
                      <p className="text-[#FFFFFF]/75 text-sm mt-3 leading-relaxed">
                        Scarica le foto, copia il testo ottimizzato per ogni portale, apri direttamente
                        il form di pubblicazione di Idealista, Immobiliare.it, Casa.it o Subito.
                      </p>
                    </div>
                    <button
                      onClick={() => setPublishOpen(true)}
                      className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#FFFFFF] text-[#7C8B6E] text-sm font-semibold tracking-wide uppercase hover:-translate-y-0.5 transition-transform whitespace-nowrap"
                      data-testid="publish-now-btn"
                    >
                      <Sparkle size={18} weight="fill" /> Pubblica subito
                    </button>
                  </div>
                  {/* Decorative dot grid */}
                  <div
                    aria-hidden
                    className="absolute -right-10 -top-10 w-64 h-64 opacity-10"
                    style={{
                      backgroundImage:
                        "radial-gradient(#FFFFFF 1.5px, transparent 1.5px)",
                      backgroundSize: "16px 16px",
                    }}
                  />
                </div>

                {/* 3️⃣ Top 3 problemi · compattato */}
                {Array.isArray(autopilot.problems) && autopilot.problems.length > 0 && (
                  <div className="card-flat p-5 lg:p-6" data-testid="autopilot-problems">
                    <span className="overline">Cosa frena i contatti</span>
                    <h3 className="font-editorial text-xl mt-1.5 mb-2">
                      Stai perdendo richieste · <span className="italic-accent text-[#7C8B6E]">ecco perché</span>
                    </h3>
                    <p className="text-[12.5px] text-[#6B6A65] mb-4 leading-snug">
                      Sistema questi punti prima di pubblicare per non perdere contatti caldi.
                    </p>
                    <div className="space-y-3">
                      {autopilot.problems.slice(0, 3).map((p, i) => (
                        <div key={i} className="border-l-2 border-[#7C8B6E] pl-4 py-1.5" data-testid={`problem-${i}`}>
                          <div className="text-[13.5px] font-semibold text-[#1C1C1A]">{p.what}</div>
                          <div className="text-[12.5px] text-[#6B6A65] mt-0.5 leading-snug">{p.why}</div>
                          <div className="text-[12.5px] text-[#7C8B6E] mt-1.5 flex items-start gap-1.5">
                            <ArrowRight size={12} weight="bold" className="mt-0.5 flex-shrink-0" />
                            <span><strong>Fix:</strong> {p.fix}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Toggle "Mostra di più" — score, checklist, short, seller mode */}
                <div className="flex justify-center pt-2">
                  <button
                    onClick={() => setAutopilotShowMore((v) => !v)}
                    className="text-sm text-[#7C8B6E] underline underline-offset-4 hover:opacity-80"
                    data-testid="autopilot-toggle-more"
                  >
                    {autopilotShowMore ? "Mostra meno" : "Mostra dettagli avanzati"}
                  </button>
                </div>

                {autopilotShowMore && (
                  <>
                    {/* Quality Score (secondario) */}
                    <div className="card-flat p-8 grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="autopilot-score">
                      <div className="lg:col-span-4">
                        <span className="overline">Quality Score</span>
                        <div className="mt-3 flex items-baseline gap-2">
                          <span className="font-editorial text-7xl text-[#7C8B6E] leading-none" data-testid="score-value">{autopilot.quality_score}</span>
                          <span className="text-2xl text-[#6B6A65]">/100</span>
                        </div>
                        <div className="mt-4 h-2 bg-[#E5E7E0] overflow-hidden rounded-full">
                          <div className="h-full bg-[#7C8B6E] transition-all duration-700" style={{ width: `${autopilot.quality_score}%` }} />
                        </div>
                      </div>
                      <div className="lg:col-span-8 flex flex-col justify-center">
                        <p className="text-base lg:text-lg leading-relaxed text-[#1C1C1A]">
                          {autopilot.score_summary}
                        </p>
                        <div className="mt-5 flex flex-wrap gap-3">
                          <button onClick={() => setSellerMode(true)} className="btn-outline text-sm" data-testid="seller-mode-btn">
                            <Users size={16} weight="bold" /> Mostra al venditore
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Versione corta per portali */}
                    {autopilot.short && (
                      <div className="card-flat p-8" data-testid="autopilot-short">
                        <span className="overline">Versione corta · portali</span>
                        <h3 className="font-editorial text-2xl mt-2 mb-5">Per Subito.it / WhatsApp</h3>
                        <CopyField label="Testo breve" value={autopilot.short} testId="copy-ap-short" multiline />
                      </div>
                    )}

                    {/* Checklist completa */}
                    {Array.isArray(autopilot.checklist) && autopilot.checklist.length > 0 && (
                      <div className="card-flat p-8" data-testid="autopilot-checklist">
                        <span className="overline">Queste modifiche possono aumentare le richieste</span>
                        <h3 className="font-editorial text-2xl mt-2 mb-5">Migliora subito</h3>
                        <ul className="space-y-2">
                          {autopilot.checklist.map((c, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm" data-testid={`checklist-${i}`}>
                              <Check size={18} weight="bold" className="text-[#7C8B6E] mt-0.5 flex-shrink-0" />
                              <span>{c}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
  );
}
